import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/site";
import { CheckCircle2, Sun, Sunset } from "lucide-react";

const PREFERENCES = [
  { value: "Mattina", icon: Sun },
  { value: "Pomeriggio", icon: Sunset },
];

export default function Booking() {
  const [preference, setPreference] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preference) {
      toast.error("Indica la tua preferenza: mattina o pomeriggio");
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Completa tutti i campi obbligatori");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        ...form,
        service: "Consulto",
        preference,
      });
      setSuccess(res.data);
      toast.success("Richiesta inviata con successo");
      setForm({ name: "", email: "", phone: "", note: "" });
      setPreference("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante l'invio della richiesta";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="prenota"
      data-testid="booking-section"
      className="py-20 lg:py-32 bg-[#E9EFE4]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <span className="eyebrow" data-testid="booking-eyebrow">Prenotazione online</span>
          <h2
            className="font-serif-elegant mt-6 text-3xl lg:text-5xl font-light tracking-tight text-[#3D3B3A] leading-tight"
            data-testid="booking-title"
          >
            Scegli il tuo<br />
            <span className="italic text-[#6B8568]">momento</span>.
          </h2>
          <p className="mt-6 text-[#73706E] text-base lg:text-lg leading-relaxed">
            Compila il modulo indicando se preferisci la mattina o il pomeriggio.
            Ti contatterò personalmente per concordare insieme giorno e orario.
          </p>
        </div>

        {success ? (
          <div
            data-testid="booking-success"
            className="bg-[#F6F8F3] border border-[#CFD9C5] rounded-3xl p-10 lg:p-16 text-center max-w-2xl mx-auto"
          >
            <CheckCircle2 size={56} strokeWidth={1} className="mx-auto text-[#9DB39A]" />
            <h3 className="font-serif-elegant text-3xl text-[#3D3B3A] mt-6">
              Grazie {success.name}.
            </h3>
            <p className="mt-4 text-[#73706E] leading-relaxed">
              Ho ricevuto la tua richiesta di appuntamento con preferenza:{" "}
              <strong className="text-[#3D3B3A]">{success.preference}</strong>.
              Ti contatterò al più presto per concordare insieme giorno e orario.
            </p>
            <button
              onClick={() => setSuccess(null)}
              data-testid="booking-new"
              className="mt-8 inline-flex items-center text-sm text-[#3D3B3A] border-b border-[#3D3B3A] hover:border-[#7E9C7B] hover:text-[#7E9C7B] pb-1 transition-colors"
            >
              Invia una nuova richiesta
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-[#F6F8F3] border border-[#CFD9C5] rounded-3xl p-6 lg:p-12 max-w-3xl space-y-8"
          >
            <div>
              <p className="eyebrow mb-4">1 — Quando preferisci?</p>
              <div className="grid grid-cols-2 gap-3 max-w-md" data-testid="booking-preferences">
                {PREFERENCES.map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPreference(value)}
                    data-testid={`booking-preference-${value.toLowerCase()}`}
                    className={`flex items-center justify-center gap-2 rounded-full border px-5 py-3 text-sm transition-all duration-200 ${
                      preference === value
                        ? "bg-[#9DB39A] text-white border-[#9DB39A]"
                        : "border-[#CFD9C5] text-[#3D3B3A] hover:border-[#9DB39A] bg-white"
                    }`}
                  >
                    <Icon size={16} strokeWidth={1.5} />
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              <p className="eyebrow">2 — I tuoi dati</p>

              <div>
                <Label htmlFor="name" className="text-xs text-[#73706E]">Nome e cognome *</Label>
                <Input
                  id="name"
                  data-testid="booking-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="email" className="text-xs text-[#73706E]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="booking-input-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs text-[#73706E]">Telefono *</Label>
                  <Input
                    id="phone"
                    data-testid="booking-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="note" className="text-xs text-[#73706E]">Motivo dell&apos;appuntamento (opzionale)</Label>
                <Textarea
                  id="note"
                  data-testid="booking-input-note"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={3}
                  className="mt-2 bg-transparent border border-[#CFD9C5] rounded-xl focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                  placeholder="Raccontami brevemente il motivo per cui vorresti un appuntamento"
                />
              </div>
            </div>

            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="booking-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 accent-[#9DB39A]"
                  required
                />
                <span className="text-xs text-[#73706E] leading-relaxed">
                  Ho letto e accetto la{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#7E9C7B] underline">
                    Privacy Policy
                  </a>{" "}
                  e acconsento al trattamento dei miei dati personali per finalità di
                  gestione della prenotazione (art. 6 GDPR). *
                </span>
              </label>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="inline-flex items-center bg-[#9DB39A] text-white hover:bg-[#7E9C7B] disabled:opacity-60 rounded-full px-8 py-4 text-sm tracking-wide transition-all duration-300"
              >
                {submitting ? "Invio in corso…" : "Invia richiesta"}
              </button>
              <p className="text-xs text-[#73706E] max-w-xs">
                I tuoi dati sono trattati con la massima riservatezza nel rispetto del segreto professionale.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
